import { base44 } from '@/api/base44Client';
import { withoutRetiredMilestones } from '@/lib/standardMilestones';

/**
 * The portfolio-wide lists, defined once.
 *
 * The API answers a list with 100 rows when it is given no limit and never
 * more than 5000, and it does not say when it stopped early. The Dashboard,
 * Projects, Reports, Bulk Updates and Import pages each used to ask for their
 * own number - 100, 200, 500 - under the same cache key, so a page showed
 * whatever the last page to load had asked for, and the Dashboard counted only
 * the 100 newest projects. Every page now asks for the whole list, under one
 * key, the same way.
 *
 * FULL_LIST_LIMIT is the API's MAX_LIMIT in api/src/lib/repo.ts; a test keeps
 * the two equal. Use it too for anything read per project (a project's files,
 * tasks, milestones, plans, daily reports), which would otherwise stop at 100.
 */
export const FULL_LIST_LIMIT = 5000;

/* Long enough that moving between pages does not refetch every list, short
   enough that a change made by someone else shows within a minute. A change
   made here refreshes at once: the pages invalidate these keys after a save. */
const LIST_STALE_MS = 60_000;

export const projectsQuery = {
  queryKey: ['projects'],
  queryFn: () => base44.entities.Project.list('-created_date', FULL_LIST_LIMIT),
  staleTime: LIST_STALE_MS,
};

export const tasksQuery = {
  queryKey: ['tasks'],
  queryFn: () => base44.entities.Task.list('-created_date', FULL_LIST_LIMIT),
  staleTime: LIST_STALE_MS,
};

export const milestonesQuery = {
  queryKey: ['milestones'],
  queryFn: () => base44.entities.Milestone.list('-created_date', FULL_LIST_LIMIT).then(withoutRetiredMilestones),
  staleTime: LIST_STALE_MS,
};

export const plansQuery = {
  queryKey: ['plans'],
  queryFn: () => base44.entities.PlanSubmission.list('-created_date', FULL_LIST_LIMIT),
  staleTime: LIST_STALE_MS,
};

/* The Dashboard's recent activity. Shorter than the lists: nothing invalidates
   it after a save, so this is what decides how soon a change made here shows
   on the Dashboard - and it must still outlast the early start below, or the
   Dashboard would fetch it a second time on arrival. */
export const activitiesQuery = {
  queryKey: ['activities'],
  queryFn: () => base44.entities.ActivityLog.list('-created_date', 20),
  staleTime: 30_000,
};

/* The property list, for the Dashboard's portfolio filter. The Properties page
   invalidates this key after every change, so a minute does not hide one. */
export const propertiesQuery = {
  queryKey: ['properties'],
  queryFn: () => base44.entities.Property.list('name', 500),
  staleTime: LIST_STALE_MS,
};

/* The user directory, for the Dashboard's Awaiting Revisions list: a project
   stores its Property Manager as an email, and the list names the person. The
   same key and call as the project form's pickers and Reports. */
export const usersQuery = {
  queryKey: ['users'],
  queryFn: () => base44.entities.User.list(),
  staleTime: LIST_STALE_MS,
};

/** Everything the Dashboard reads, in one place, so the early start below and the page cannot drift apart. */
export const DASHBOARD_QUERIES = [
  projectsQuery,
  tasksQuery,
  milestonesQuery,
  plansQuery,
  activitiesQuery,
  propertiesQuery,
  usersQuery,
];

/** Everything My Day reads besides /me - a subset of the Dashboard's, under the same keys. */
export const MY_DAY_QUERIES = [
  projectsQuery,
  tasksQuery,
  milestonesQuery,
  plansQuery,
];

/* The pages the Hub opens on, by lower-case path. `''` is the bare address,
   which App.jsx sends to My Day. */
const START_PAGE_QUERIES = {
  '': MY_DAY_QUERIES,
  '/myday': MY_DAY_QUERIES,
  '/dashboard': DASHBOARD_QUERIES,
};

/**
 * Start the landing page's lists while /me is still being answered.
 *
 * Opening the Hub used to be two round trips end to end: /me, and only once
 * the app had rendered on its answer, the page's lists. The lists do not
 * depend on that answer - the API decides what each caller may see from their
 * token - so on My Day and the Dashboard they now start at the same moment.
 * Any other page has its own data and is left alone. A failure here is not
 * reported: the page's own query fetches again and shows its own error.
 */
export function prefetchStartPage(queryClient, pathname) {
  const path = (pathname ?? '').replace(/\/+$/, '').toLowerCase();
  for (const query of START_PAGE_QUERIES[path] ?? []) queryClient.prefetchQuery(query);
}

/** Most recently changed first - the order the bulk update sheet lists projects in. */
export function newestUpdatedFirst(projects) {
  return [...(projects ?? [])].sort((a, b) =>
    String(b.updated_date ?? '').localeCompare(String(a.updated_date ?? '')));
}
